import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import * as CopyText from '@segment/matcha/recipes/CopyText'

import HeroBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'
import { SignupButtonText } from '@segment/matcha/recipes/SignupButton'

import DemoForm from '@segment/templates/Forms/DemoForm'

import heroPng2x from './assets/Hero@2x.png'
import heroWebp from './assets/Hero.webp'
import heroWebp2x from './assets/Hero@2x.webp'

const copy = {
  overline: `Functions`,
  heading: `Endless customization at your fingertips with just a few lines of Javascript`,
  description: `Build custom integrations, enrich and transform data, and mask sensitive information with a simple webhook on Twilio Segment’s open, extensible platform - now even faster with AI.`,
  linkText: `Create a free account`,
}

const Hero: FC<PropsWithChildren<unknown>> = () => {
  const afterContent = (
    <SignupButtonText size="medium" tag="a" iconEnd={ChevronRightIcon}>
      {copy.linkText}
    </SignupButtonText>
  )

  return (
    <Wrapper>
      <HeroBase variant="2Col">
        <StyledHeroRecipeCopy>
          <CopyText.Overline tag="h5">{copy.overline}</CopyText.Overline>
          <CopyText.Heading variant="h1" tag="h1">
            {copy.heading}
          </CopyText.Heading>
          <CopyText.Description variant="pExtraLarge" tag="p">
            {copy.description}
          </CopyText.Description>
          <StyledHeroRecipeForm>
            <DemoForm
              isProgressive={true}
              customFormIdentifier="site-functions-demo"
              successMessageAppearance="twilioLight"
              after={afterContent}
            />
          </StyledHeroRecipeForm>
        </StyledHeroRecipeCopy>

        <HeroRecipe.Contents>
          <Picture>
            <source type="image/webp" srcSet={`${heroWebp}, ${heroWebp2x} 2x`} />
            <HeroRecipe.Image width="640" height="584" src={heroPng2x} alt="" loading="lazy" />
          </Picture>
        </HeroRecipe.Contents>
      </HeroBase>
    </Wrapper>
  )
}

const Picture = styled.picture`
  filter: drop-shadow(${({ theme }) => theme.shadows[2]});
`

const StyledHeroRecipeForm = styled(HeroRecipe.Form)`
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    margin-left: initial;
    margin-right: initial;
  }
`

const StyledHeroRecipeCopy = styled(HeroRecipe.Copy)`
  height: 100%;

  ${({ theme }) => theme.breakpoints.down(`md`)} {
    text-align: left;
  }
`

const Wrapper = styled.header`
  background-color: ${({ theme }) => theme.palette.twilio.green[10]};
`

export default Hero
